import React, { useEffect, useState } from "react";
import { IonIcon, IonToast } from "@ionic/react";
import PageLayout from "../../layouts/PageLayout";
import { FirebaseAnalytics } from "@ionic-native/firebase-analytics";
import { useHistory } from "react-router";
import http from "axios";
import { useDispatch } from "react-redux";
import { Dispatch } from "../../store";
import ErrorMessage from "components/ErrorMessage";
import Title from "components/Title";
import Input, { PasswordInput } from "components/Input";
import Button from "components/Button";
import BetterModal from "components/BetterModal";
import { FirmwareStatus, UserToken } from "../../model";
import { checkmarkCircle, removeCircle } from "ionicons/icons";

type StatesDict = {
  [name: string]: [since: number, state: any];
};

interface LookupMacFlagsResponse {
  mac: string;
  serial: string;
  carton: number;
  brand: string;
  trial_consumed: boolean;
  claimed_codes: string[];
  return_count: number;
  payment_method_required: boolean;
  created: number;
}

interface StateResponse {
  result: string;
  state: string;
}

interface UIState {
  savedState: string;
  currentState: string;
  currentStateIsValid: boolean;
}

type UIStatesDict = {
  [name: string]: UIState;
};

///////////////////////////////////////////////////////////////
//   MAIN COMPONENT
///////////////////////////////////////////////////////////////

const ManageMacFlags: React.FC<{}> = () => {
  const history = useHistory();
  const dispatch = useDispatch<Dispatch>();

  const [lookupString, setLookupString] = useState<string>("");
  const [foundMacFlags, setFoundMacFlags] = useState<LookupMacFlagsResponse>();
  const [lookupError, setLookupError] = useState<string>("");
  const [toastMessage, setToastMessage] = useState<string>("");
  const [macFlagsStates, setMacFlagsStates] = useState<UIStatesDict>({});

  useEffect(() => {
    FirebaseAnalytics.logEvent("init_page", { page: "admin_manage_accounts" });
  }, []);

  useEffect(() => {
    if (foundMacFlags !== undefined) {
      let states: UIStatesDict = {};
      Object.entries(foundMacFlags).forEach(([name, value]) => {
        if (
          name !== "mac" &&
          name !== "serial" &&
          name !== "carton" &&
          name !== "created"
        ) {
          let fieldValue = value;
          if (name === "claimed_codes") {
            fieldValue = value.toString();
          }
          states[name] = {
            savedState: fieldValue,
            currentState: fieldValue,
            currentStateIsValid: true,
          };
        }
      });
      setMacFlagsStates(states);
    }
  }, [foundMacFlags]);

  const handleLookupMacFlags = () => {
    setFoundMacFlags(undefined);
    setLookupError("");
    setToastMessage("");

    const body = {
      lookup_string: lookupString,
    };

    http
      .post<LookupMacFlagsResponse>(`/v1/admin/macflags`, body)
      .then((result) => {
        console.log(result.data);
        setFoundMacFlags(result.data);
      })
      .catch((error) => {
        setLookupError("Error looking up mac flags");
      });
  };

  const handleSaveState = (
    target: string,
    id: string,
    state_name: string,
    state: string | null
  ) => {
    setToastMessage("");

    let state_value: any = state;
    if (state_name === "claimed_codes") {
      state_value = state!.split(",") || [];
      console.log("state value: ", state_value);
      state_value = state_value.map((stringToTrim: string) =>
        stringToTrim.trim()
      );
      console.log("state value post trim: ", state_value);
    }
    const body = {
      lookup_string: id,
      [state_name]: state_value,
    };

    http
      .post<StateResponse>(`/v1/admin/macflags`, body)
      .then((result) => {
        setToastMessage("Mac Flags Updated!");
        if (target === "macFlags") {
          let newMacFlagsState = {
            ...macFlagsStates[state_name],
            savedState: result.data.state,
            currentState: result.data.state,
          };
          setMacFlagsStates({
            ...macFlagsStates,
            [state_name]: newMacFlagsState,
          });
        }
      })
      .catch((error) => {
        setToastMessage("Error in save " + target + " state");
      });
  };

  const handleStateChange = (
    target: string,
    state_name: string,
    state: string
  ) => {
    let newState: UIState;

    if (target === "macFlags")
      newState = { ...macFlagsStates[state_name], currentState: state };
    else return;

    if (state_name === "claimed_codes") {
      if (state === "") {
        newState.currentStateIsValid = true;
      } else {
        let claimedArray = state.split(",");
        let regex = /^(?=.*[\w\d]).+/;
        if (
          claimedArray.every(
            (code) => regex.test(code) || claimedArray.length === 0
          )
        ) {
          newState.currentStateIsValid = true;
        } else {
          newState.currentStateIsValid = false;
        }
      }
    }
    if (
      state_name === "trial_consumed" ||
      state_name === "payment_method_required"
    ) {
      if (state === "true" || state === "false") {
        newState.currentStateIsValid = true;
      } else {
        newState.currentStateIsValid = false;
      }
    }
    if (state_name === "return_count") {
      if (isNaN(parseInt(state))) {
        newState.currentStateIsValid = false;
      } else {
        newState.currentStateIsValid = true;
      }
    }
    if (state_name === "brand") {
      if (state !== null && state.length > 0 && state.length < 16) {
        newState.currentStateIsValid = true;
      } else {
        newState.currentStateIsValid = false;
      }
    }
    if (target === "macFlags")
      setMacFlagsStates({ ...macFlagsStates, [state_name]: newState });
    else return;
  };

  return (
    <PageLayout showEnvironment={true}>
      <Title title="Manage Mac Flags" />
      <div className="flex flex-row items-center my-2 gap-3">
        <Input
          className="w-full"
          id="lookup_string"
          label=""
          placeholder="Enter MAC address or serial number"
          type="text"
          inputProps={{
            value: lookupString,
            onChange: (e: any) => setLookupString(e.target.value),
          }}
        />
        <Button
          className="w-full"
          disabled={lookupString === ""}
          onClick={() => handleLookupMacFlags()}
        >
          Lookup Mac Flags
        </Button>
      </div>
      <ErrorMessage isText error={lookupError} />

      {foundMacFlags && (
        <>
          <p className="my-5 text-md">
            <b>MAC:</b> {foundMacFlags.mac}
          </p>
          <p className="my-5 text-md">
            <b>Serial:</b> {foundMacFlags.serial || "NULL"} [Carton{" "}
            {foundMacFlags.carton || "NULL"}]
          </p>
          <p className="my-5 text-md">
            <b>Created:</b> {new Date(foundMacFlags.created).toLocaleString()}
          </p>
          <StateTable
            states={macFlagsStates}
            onStateChange={(state_name, state) =>
              handleStateChange("macFlags", state_name, state)
            }
            onSaveState={(state_name, state) =>
              handleSaveState("macFlags", foundMacFlags.mac, state_name, state)
            }
            onDeleteState={(state_name) =>
              handleSaveState("macFlags", foundMacFlags.mac, state_name, null)
            }
          />
        </>
      )}

      <IonToast
        color="success"
        isOpen={!!toastMessage.length}
        onDidDismiss={() => setToastMessage("")}
        message={toastMessage}
        duration={5000}
      />
    </PageLayout>
  );
};

export default ManageMacFlags;

///////////////////////////////////////////////////////////////
//   STATE TABLE
///////////////////////////////////////////////////////////////

interface StateTableProps {
  states: UIStatesDict;
  onStateChange(state_name: string, state: string): void;
  onSaveState(state_name: string, state: string): void;
  onDeleteState(state_name: string): void;
}

const StateTable: React.FC<StateTableProps> = ({
  states,
  onStateChange,
  onSaveState,
  onDeleteState,
}) => {
  return (
    <table>
      <thead className="text-left text-sm">
        <tr key="adapter_states_header">
          <th className="p-1">Name</th>
          <th className="p-1">State</th>
          <th className="p-1"></th>
        </tr>
      </thead>
      <tbody className="text-xs">
        {Object.entries(states).map(([name, state]) => (
          <tr key={"adapter_state_" + name}>
            <td className="p-1">{name}</td>
            <td className="p-1 w-full">
              <Input
                id={"adapter_state_" + name}
                label=""
                placeholder=""
                type="text"
                textSize="xs"
                error={state.currentStateIsValid ? "" : "Invalid value"}
                inputProps={{
                  value: state.currentState,
                  onChange: (e: any) => onStateChange(name, e.target.value),
                }}
              />
            </td>
            <td className="p-1 align-bottom">
              <div className="flex flex-row gap-1">
                <Button
                  size="small"
                  variant="white"
                  onClick={() => onSaveState(name, state.currentState)}
                  disabled={
                    !state.currentStateIsValid ||
                    state.savedState === state.currentState
                  }
                >
                  <IonIcon
                    icon={checkmarkCircle}
                    className="h-6 w-6 text-green-600"
                  />
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
